import { Head, Link, router } from "@inertiajs/react"
import { ChevronDown, CirclePlus } from "lucide-react"

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import AppLayout from "@/layouts/app-layout"
import { ensureUrl } from "@/lib/format-url"
import type { BreadcrumbItem } from "@/types"

/* ── Top Bar Helpers ────────────────────────────────────── */

function TabLink({
  href,
  children,
  className = "",
}: {
  href: string
  children: React.ReactNode
  className?: string
}) {
  return (
    <a
      href={href}
      className={`text-hpi-med-blue hover:text-hpi-active-blue flex items-center gap-1 text-sm transition-colors ${className}`}
    >
      {children}
    </a>
  )
}

/* ── Types ──────────────────────────────────────────────── */

interface Vendor {
  id: number
  display_name: string
  company_name: string | null
  sort_name: string | null
  first_name: string | null
  last_name: string | null
  vendor_type_id: number | null
  vendor_type: string | null
  vendor_type_group_id: number | null
  country_id: number | null
  country: string | null
  address: string | null
  address2: string | null
  city: string | null
  state: string | null
  zip: string | null
  office_phone: string | null
  cell_phone: string | null
  fax: string | null
  email: string | null
  website: string | null
  notes: string | null
  hourly_rate: number | null
  travel_rate: number | null
  mileage_rate: number | null
  schedule: boolean
  status: number
  contacts_count: number
  documents_count: number
  logs_count: number
  schedule_count: number
  created_at: string
  updated_at: string
}

interface VendorContact {
  id: number
  display_name: string
  title: string | null
  phone: string | null
  cell_phone: string | null
  email: string | null
  city: string | null
  state: string | null
}

interface Props {
  vendor: Vendor
  contacts: VendorContact[]
  can_destroy: boolean
}

export default function Show({ vendor, contacts, can_destroy }: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Subcontractors", href: "/company/vendors" },
    { title: vendor.display_name, href: `/company/vendors/${vendor.id}` },
  ]

  const handleDelete = () => {
    if (confirm("Are you sure you want to delete this subcontractor?")) {
      router.delete(`/company/vendors/${vendor.id}`)
    }
  }

  const formatCurrency = (value: number | null) => {
    if (value == null) return "-"
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(value)
  }

  const addItems = [
    { label: "Contact", href: `/company/vendors/${vendor.id}/contacts/new` },
    { label: "Document", href: `/company/vendors/${vendor.id}/documents/new` },
    { label: "Log", href: `/company/vendors/${vendor.id}/logs/new` },
  ]

  const vendorTabsBar = (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-6">
        {vendor.contacts_count > 0 && (
          <TabLink href={`/company/vendors/${vendor.id}/contacts`}>
            Contacts ({vendor.contacts_count})
          </TabLink>
        )}
        {vendor.documents_count > 0 && (
          <TabLink href={`/company/vendors/${vendor.id}/documents`}>
            Documents ({vendor.documents_count})
          </TabLink>
        )}
        {vendor.logs_count > 0 && (
          <TabLink href={`/company/vendors/${vendor.id}/logs`}>
            Logs ({vendor.logs_count})
          </TabLink>
        )}
        {vendor.schedule_count > 0 && (
          <TabLink href={`/company/vendors/${vendor.id}/schedules`}>
            Schedules ({vendor.schedule_count})
          </TabLink>
        )}
      </div>
      <DropdownMenu>
        <DropdownMenuTrigger className="text-hpi-med-blue hover:text-hpi-active-blue flex items-center gap-1.5 text-sm outline-none">
          <CirclePlus className="h-4 w-4" />
          <span>Add</span>
          <ChevronDown className="h-3 w-3" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {addItems.map((item) => (
            <DropdownMenuItem key={item.href} asChild>
              <a href={item.href} className="cursor-pointer">
                {item.label}
              </a>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )

  return (
    <AppLayout breadcrumbs={breadcrumbs} topBar={vendorTabsBar}>
      <Head title={vendor.display_name} />

      <div className="p-6">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold">{vendor.display_name}</h1>
          </div>
          <div className="space-x-2">
            <Link
              href={`/company/vendors/${vendor.id}/edit`}
              className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white"
            >
              Edit
            </Link>
            {can_destroy && (
              <button
                onClick={handleDelete}
                className="bg-hpi-red hover:bg-hpi-red/80 rounded px-4 py-2 text-white"
              >
                Delete
              </button>
            )}
          </div>
        </div>

        <div className="mb-6 grid grid-cols-1 gap-6 md:grid-cols-2">
          <div className="border-hpi-light-gray rounded border p-4">
            <h2 className="mb-4 text-lg font-semibold">Basic Information</h2>
            <dl className="grid grid-cols-2 gap-2">
              <dt className="text-hpi-med-blue font-medium">Company</dt>
              <dd>{vendor.company_name ?? "-"}</dd>
              <dt className="text-hpi-med-blue font-medium">Name</dt>
              <dd>
                {[vendor.first_name, vendor.last_name]
                  .filter(Boolean)
                  .join(" ") ?? "-"}
              </dd>
              <dt className="text-hpi-med-blue font-medium">Type</dt>
              <dd>{vendor.vendor_type ?? "-"}</dd>
              <dt className="text-hpi-med-blue font-medium">Country</dt>
              <dd>{vendor.country ?? "-"}</dd>
            </dl>
          </div>

          <div className="border-hpi-light-gray rounded border p-4">
            <h2 className="mb-4 text-lg font-semibold">Contact Information</h2>
            <dl className="grid grid-cols-2 gap-2">
              <dt className="text-hpi-med-blue font-medium">Office Phone</dt>
              <dd>{vendor.office_phone ?? "-"}</dd>
              <dt className="text-hpi-med-blue font-medium">Cell Phone</dt>
              <dd>{vendor.cell_phone ?? "-"}</dd>
              <dt className="text-hpi-med-blue font-medium">Fax</dt>
              <dd>{vendor.fax ?? "-"}</dd>
              <dt className="text-hpi-med-blue font-medium">Email</dt>
              <dd>{vendor.email ?? "-"}</dd>
              <dt className="text-hpi-med-blue font-medium">Website</dt>
              <dd>
                {vendor.website ? (
                  <a
                    href={ensureUrl(vendor.website)!}
                    className="text-hpi-active-blue hover:text-hpi-active-blue"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {vendor.website}
                  </a>
                ) : (
                  "-"
                )}
              </dd>
            </dl>
          </div>

          <div className="border-hpi-light-gray rounded border p-4">
            <h2 className="mb-4 text-lg font-semibold">Address</h2>
            <address className="not-italic">
              {vendor.address && <div>{vendor.address}</div>}
              {vendor.address2 && <div>{vendor.address2}</div>}
              {(vendor.city ?? vendor.state ?? vendor.zip) && (
                <div>
                  {[vendor.city, vendor.state].filter(Boolean).join(", ")}{" "}
                  {vendor.zip}
                </div>
              )}
            </address>
          </div>

          <div className="border-hpi-light-gray rounded border p-4">
            <h2 className="mb-4 text-lg font-semibold">Rates</h2>
            <dl className="grid grid-cols-2 gap-2">
              <dt className="text-hpi-med-blue font-medium">Hourly Rate</dt>
              <dd>{formatCurrency(vendor.hourly_rate)}</dd>
              <dt className="text-hpi-med-blue font-medium">Travel Rate</dt>
              <dd>{formatCurrency(vendor.travel_rate)}</dd>
              <dt className="text-hpi-med-blue font-medium">Mileage Rate</dt>
              <dd>{formatCurrency(vendor.mileage_rate)}</dd>
              <dt className="text-hpi-med-blue font-medium">On Schedule</dt>
              <dd>{vendor.schedule ? "Yes" : "No"}</dd>
            </dl>
          </div>
        </div>

        {vendor.notes && (
          <div className="border-hpi-light-gray mb-6 rounded border p-4">
            <h2 className="mb-2 text-lg font-semibold">Notes</h2>
            <p className="whitespace-pre-wrap">{vendor.notes}</p>
          </div>
        )}

        <div id="contacts" className="border-hpi-light-gray rounded border">
          <div className="border-hpi-light-gray bg-hpi-pale-gray flex items-center justify-between border-b px-4 py-2">
            <h2 className="text-lg font-semibold">Contacts</h2>
            <Link
              href={`/company/vendors/${vendor.id}/contacts/new`}
              className="text-hpi-active-blue hover:text-hpi-dark-blue text-sm font-medium"
            >
              Add Contact
            </Link>
          </div>
          {contacts.length > 0 ? (
            <table className="w-full">
              <thead>
                <tr className="border-hpi-light-gray text-hpi-med-blue border-b text-left text-sm">
                  <th className="px-4 py-2">Name</th>
                  <th className="hidden px-4 py-2 md:table-cell">Title</th>
                  <th className="hidden px-4 py-2 md:table-cell">Phone</th>
                  <th className="hidden px-4 py-2 md:table-cell">Email</th>
                </tr>
              </thead>
              <tbody>
                {contacts.map((c) => (
                  <tr
                    key={c.id}
                    className="border-hpi-light-gray/50 hover:bg-hpi-pale-blue border-b last:border-b-0"
                  >
                    <td className="px-4 py-2 text-sm">
                      <Link
                        href={`/company/vendors/${vendor.id}/contacts/${c.id}`}
                        className="text-hpi-active-blue hover:text-hpi-dark-blue font-medium"
                      >
                        {c.display_name}
                      </Link>
                    </td>
                    <td className="text-hpi-dark-gray hidden px-4 py-2 text-sm md:table-cell">
                      {c.title ?? "-"}
                    </td>
                    <td className="text-hpi-dark-gray hidden px-4 py-2 text-sm md:table-cell">
                      {c.phone || c.cell_phone || "-"}
                    </td>
                    <td className="text-hpi-dark-gray hidden px-4 py-2 text-sm md:table-cell">
                      {c.email ?? "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-hpi-med-gray px-4 py-4 text-sm">
              No contacts yet.
            </p>
          )}
        </div>
      </div>
    </AppLayout>
  )
}
